import { Box, Button, Grid, styled } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useImageContext } from '../../imageContext';
import Context from '../../context';
import {
  fbCreateImageStructure,
  fbDeleteImageStructure,
} from '../../services/service';
import { useGetToken } from '../../hooks/useGetToken';
import { usePopupContext } from '../../PopupContext';
import InputField from '../InputField';

const ButtonStyled = styled(Button)({
  borderRadius: '24px',
  padding: '6px 50px',
});

const FolderGrid = styled(Grid)({
  width: '100%',
});

const ButtonContainerBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-around',
  marginTop: '20px',
  alignItems: 'center',
  width: '100%',
});

const deleteImageTitle = 'Delete folder';
const deleteImageMessage = 'Do you want to delete folder';

export const ImageStructureDetail = (): JSX.Element => {
  const { emptyFolder, selected } = useImageContext();
  const { updateIsLoading, setShowError, setMessage: setErrorMessage } = useContext(Context);
  const [getTokenFunction] = useGetToken();

  const {
    setTrigger,
    setTitle,
    setMessage,
    setMethod,
    setAction,
    setExecuteMethod,
    executeMethod,
  } = usePopupContext();
  const { itemList, folder, setFolder, setTimestamp, setSelected } =
    useImageContext();

  const isFolder = selected.endsWith('/');
  const deleteFolderDisabled = selected === '' || !isFolder || !emptyFolder;
  const createDisabled = selected === '' || !isFolder;

  const handleCreateFolder = async () => {
    const accessToken = await getTokenFunction();
    if (!folder || !accessToken) return;

    updateIsLoading(true);
    try {
      await fbCreateImageStructure(`${folder}`, accessToken);
      setSelected(`${folder}/`);
      setFolder('');
      setTimestamp(`${new Date().toISOString()}`);
    } catch (e) {
      setErrorMessage(`fbCreateImageStructure ${e}`)
      setShowError(true);
      updateIsLoading(false);
    } finally {
      //            updateIsLoading(false);
    }
  };

  useEffect(() => {
    const execute = async () => {
      if (executeMethod === '--handleDeleteImageStructure') {
        await handleDeleteImageStructure();
        setExecuteMethod('');
      }
    };
    execute();
  }, [executeMethod]);

  const handleDeleteImageStructure = async () => {
    const accessToken = await getTokenFunction();

    if (!folder || !accessToken) return;

    updateIsLoading(true);
    await fbDeleteImageStructure(folder, accessToken)
      .then(() => {
        setSelected('');
        setTimestamp(`${new Date().toISOString()}`);
      })
      .catch((e) => {
        setErrorMessage(`fbDeleteImageStructure ${e}`)
        setShowError(true);
        updateIsLoading(false);
      });
  };

  const dCurrentFolderTitle = 'Current folder';
  const dCurrentFolderBody = 'This is an existing folder. It can be removed when it is empty.';
  const dNewFolderTitle = 'New folder';
  const dNewFolderBody = 'A new folder can be create to place images into. <><>When pressing the create button a new folder will be added into which new images can be uploaded.';

  const handleLockedFields = (
    field: string,
    locked: boolean
  ) => {
    console.error(`not used ${field} ${locked}`);
  }

  return (
    <>
      <Grid container>
        <FolderGrid>
          <Box>
            <InputField
                labelText={selected === folder && folder !== '' ? 'Current folder' : 'New folder'}
                labelId={'adjustFolder'}
                mandatory={false}
                helpTextTitle={selected === folder && folder !== '' ? dCurrentFolderTitle : dNewFolderTitle}
                helpTextBody={selected === folder && folder !== '' ? dCurrentFolderBody : dNewFolderBody}
                value={isFolder ? folder : ''}

                onBlur={() => {}}
                onChange={(value) => {
                  if (value.startsWith(selected)) {
                    setFolder(
                      `${selected}${value
                        .substring(selected.length - 1)
                        .replaceAll('/', '')}`
                    );
                  }
                }}
                fullWidth={true}
                error={false}
                disabled={!isFolder}
                onLock={handleLockedFields}
                disabledLock={true}
                displayedLock={false}
                isLocked={false}
              />
          </Box>
        </FolderGrid>
      </Grid>
      <Grid container spacing={2} sx={{width:'90%'}}>
          <ButtonContainerBox>
            <ButtonStyled
              disabled={deleteFolderDisabled || selected !== folder}
              data-testid="delete-image-structure-button"
              variant="contained"
              onClick={() => {
                setTitle(deleteImageTitle);
                setMessage(`${deleteImageMessage} ${selected}?`);
                setAction('Delete');
                setExecuteMethod('');
                setMethod('handleDeleteImageStructure');
                setTrigger(true);
              }}
            >
              Delete
            </ButtonStyled>
            &nbsp;
            <ButtonStyled
              disabled={
                createDisabled ||
                itemList.filter(
                  (item) => item === `${folder}` || item === `${folder}/`
                ).length > 0
              }
              data-testid="create-image-structure-button"
              variant="contained"
              onClick={() => {
                handleCreateFolder();
              }}
            >
              Create
            </ButtonStyled>
          </ButtonContainerBox>
      </Grid>
    </>
  );
};
